/**
 * PipeSizing.js
 * Pipe sizing calculations based on flow rate and velocity.
 * All inputs and outputs are in Imperial units (GPM, inches, ft/s).
 */

import { getPipeSizeTable } from './PipeSizingData.js';

/**
 * Calculate velocity for a given flow rate and pipe internal diameter.
 * @param {number} flowRateGPM  - Flow rate in GPM
 * @param {number} diameterIn   - Pipe internal diameter in inches
 * @returns {number} Velocity in ft/s
 */
export function calculateVelocity(flowRateGPM, diameterIn) {
  // velocity (ft/s) = flowRate (ft³/s) / area (ft²)
  // flowRate (ft³/s) = GPM / 448.831
  // area (ft²) = π × (diameter_ft / 2)²
  const diameterFt = diameterIn / 12;
  const areaFt2 = Math.PI * (diameterFt / 2) ** 2;
  return (flowRateGPM / 448.831) / areaFt2;
}

/**
 * Get target velocity range based on fluid temperature.
 * Always expects temperature in °F and returns velocities in ft/s.
 * @param {number} avgFluidTempF - Average fluid temperature in °F
 * @returns {object} {min, max, target} velocities in ft/s
 */
function getTargetVelocityRange(avgFluidTempF) {
  if (avgFluidTempF > 80) {
    // Heating water: 2–4 ft/s, exact midpoint = 3.0 ft/s
    return { min: 2.0, max: 4.0, target: 3.0 };
  } else {
    // Chilled water: 2–8 ft/s, exact midpoint = 5.0 ft/s
    return { min: 2.0, max: 8.0, target: 5.0 };
  }
}

/**
 * Auto-size pipe based on flow rate and fluid temperature.
 *
 * Algorithm:
 *   1. Derive ideal internal diameter (inches) from target velocity.
 *   2. Locate the pair of consecutive pipe IDs that bracket the ideal diameter.
 *   3. Apply the 1/3-point demarcation rule: if ideal < (smaller + (larger-smaller)/3),
 *      select the smaller size; otherwise select the larger size.
 *
 * Pass isMetric to select the appropriate display-name table (metric or imperial
 * pipe labeling). All sizing math is performed in inches regardless.
 *
 * @param {number} flowRateGPM   - Flow rate in GPM
 * @param {string} material      - Pipe material ('Steel', 'Copper', 'PEX')
 * @param {number} avgFluidTempF - Average fluid temperature in °F
 * @param {boolean} isMetric     - Select metric display-name table when true
 * @returns {{ displaySize: string, id: number, velocity: number }}
 *   id       = selected pipe internal diameter in inches
 *   velocity = velocity through selected pipe in ft/s
 */
export function autoSizePipe(flowRateGPM, material, avgFluidTempF, isMetric) {
  const pipeSizes = getPipeSizeTable(material, isMetric);
  const targetFtS = getTargetVelocityRange(avgFluidTempF).target;

  // Ideal internal diameter in inches
  const flowFt3s = flowRateGPM / 448.831;
  const areaFt2 = flowFt3s / targetFtS;
  const idealDiameterIn = 24 * Math.sqrt(areaFt2 / Math.PI);

  // Normalize table IDs to inches (metric tables store IDs in mm)
  const sizes = pipeSizes.map(s => ({
    displaySize: s.displaySize,
    idIn: isMetric ? s.id / 25.4 : s.id,
  }));

  // Clamp to table extremes
  if (idealDiameterIn <= sizes[0].idIn) {
    const s = sizes[0];
    return { displaySize: s.displaySize, id: s.idIn, velocity: calculateVelocity(flowRateGPM, s.idIn) };
  }
  if (idealDiameterIn >= sizes[sizes.length - 1].idIn) {
    const s = sizes[sizes.length - 1];
    return { displaySize: s.displaySize, id: s.idIn, velocity: calculateVelocity(flowRateGPM, s.idIn) };
  }

  // Bracket and apply 1/3 demarcation rule
  for (let i = 0; i < sizes.length - 1; i++) {
    const smaller = sizes[i];
    const larger  = sizes[i + 1];
    if (idealDiameterIn >= smaller.idIn && idealDiameterIn < larger.idIn) {
      const demarcation = smaller.idIn + (larger.idIn - smaller.idIn) / 3;
      const selected = idealDiameterIn < demarcation ? smaller : larger;
      return {
        displaySize: selected.displaySize,
        id: selected.idIn,
        velocity: calculateVelocity(flowRateGPM, selected.idIn),
      };
    }
  }

  // Fallback (should never reach here)
  const last = sizes[sizes.length - 1];
  return { displaySize: last.displaySize, id: last.idIn, velocity: calculateVelocity(flowRateGPM, last.idIn) };
}

/**
 * Format velocity value for display
 * @param {number} velocity - Velocity in ft/s or m/s
 * @returns {string} Formatted velocity string
 */
export function formatVelocity(velocity) {
  return velocity.toFixed(2);
}
